import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Separator } from './ui/separator';
import ConfirmDialog from './ConfirmDialog';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { Plus, Edit, Trash2, Sigma, Search as SearchIcon } from 'lucide-react';

const FORMATS = [
  ['number', 'Number'],
  ['currency', 'Currency'],
  ['percentage', 'Percentage'],
];

const EMPTY_FORM = {
  name: '',
  code: '',
  formula: '',
  format: 'number',
  decimals: 2,
};

function slugify(name) {
  return (name || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export default function CustomMetrics() {
  const { t } = useT();
  const [baseMetrics, setBaseMetrics] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API + '/custom-metrics');
      setBaseMetrics(res.data.base_metrics || []);
      setMetrics(res.data.custom || []);
    } catch (err) {
      console.error('Error loading custom metrics:', err);
      toast.error(t('Error loading custom metrics'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm(EMPTY_FORM);

  const insertMetric = (m) =>
    setForm((prev) => ({
      ...prev,
      formula: prev.formula ? prev.formula + ' ' + m : m,
    }));

  const payloadFromForm = () => ({
    name: form.name.trim(),
    code: (form.code || '').trim() || slugify(form.name),
    formula: form.formula.trim(),
    format: form.format,
    decimals: Number(form.decimals) || 0,
  });

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error(t('Name is required'));
      return;
    }
    if (!form.formula.trim()) {
      toast.error(t('Formula is required'));
      return;
    }
    try {
      const res = await axios.post(API + '/custom-metrics', payloadFromForm());
      setMetrics((prev) => [res.data, ...prev]);
      setCreateOpen(false);
      resetForm();
      toast.success(t('Custom metric created'));
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      console.error('Error creating custom metric:', err);
      toast.error(detail || t('Error creating custom metric'));
    }
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name || '',
      code: m.code || '',
      formula: m.formula || '',
      format: m.format || 'number',
      decimals: m.decimals != null ? m.decimals : 2,
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editing) return;
    if (!form.name.trim()) {
      toast.error(t('Name is required'));
      return;
    }
    if (!form.formula.trim()) {
      toast.error(t('Formula is required'));
      return;
    }
    try {
      const res = await axios.put(API + '/custom-metrics/' + editing.id, payloadFromForm());
      setMetrics((prev) => prev.map((m) => (m.id === editing.id ? res.data : m)));
      setEditOpen(false);
      setEditing(null);
      resetForm();
      toast.success(t('Custom metric updated'));
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      console.error('Error updating custom metric:', err);
      toast.error(detail || t('Error updating custom metric'));
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await axios.delete(API + '/custom-metrics/' + id);
      setMetrics((prev) => prev.filter((m) => m.id !== id));
      setDeleteTarget(null);
      toast.success(t('Custom metric deleted'));
    } catch (err) {
      console.error('Error deleting custom metric:', err);
      toast.error(t('Error deleting custom metric'));
    }
  };

  const filtered = metrics.filter((m) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (m.name || '').toLowerCase().includes(q) ||
      (m.code || '').toLowerCase().includes(q) ||
      (m.formula || '').toLowerCase().includes(q)
    );
  });

  const formatLabel = (fmt) => {
    const found = FORMATS.find((f) => f[0] === fmt);
    return found ? t(found[1]) : fmt;
  };

  const renderForm = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Name')}</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={t('e.g. Unique CR')}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('Code (slug)')}</Label>
          <Input
            value={form.code}
            onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
            placeholder={slugify(form.name) || 'unique_cr'}
          />
          <p className="text-xs text-muted-foreground">
            {t('Column key in reports. Auto-generated from the name if left blank.')}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t('Formula')}</Label>
        <Input
          value={form.formula}
          onChange={(e) => setForm((prev) => ({ ...prev, formula: e.target.value }))}
          placeholder="conversions / unique_clicks * 100"
          className="font-mono"
        />
        <p className="text-xs text-muted-foreground">
          {t('Arithmetic over base metrics. Supported: + - * / ( ) and numbers.')}
        </p>
      </div>

      <Separator />

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">
          {t('Base metrics')}
        </Label>
        <div className="flex flex-wrap gap-1.5">
          {baseMetrics.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => insertMetric(m)}
              className="px-2 py-1 rounded border border-border text-xs font-mono text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <Separator />

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Format')}</Label>
          <Select value={form.format} onValueChange={(v) => setForm((prev) => ({ ...prev, format: v }))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FORMATS.map(([v, l]) => (
                <SelectItem key={v} value={v}>{t(l)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('Decimals')}</Label>
          <Input
            type="number"
            min={0}
            max={6}
            value={form.decimals}
            onChange={(e) => setForm((prev) => ({ ...prev, decimals: e.target.value }))}
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Analytics')}
        title={t('Custom metrics')}
        subtitle={t('Define formula-based report columns over base metrics (CR, EPC, ROI variants).')}
        actions={
          <Dialog
            open={createOpen}
            onOpenChange={(open) => {
              setCreateOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                {t('Create metric')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('Create custom metric')}</DialogTitle>
                <DialogDescription>
                  {t('A formula over base metrics rendered as a report column.')}
                </DialogDescription>
              </DialogHeader>
              {renderForm()}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                  {t('Cancel')}
                </Button>
                <Button onClick={handleCreate}>{t('Create')}</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('Search custom metrics')}
            className="pl-9"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <CardContent className="py-10">
                <XpnsrEmpty
                  icon={Sigma}
                  title={t('No custom metrics yet')}
                  subtitle={t('Create one like uCR = conversions / unique_clicks * 100.')}
                />
              </CardContent>
            </Card>
          </div>
        ) : (
          filtered.map((m) => (
            <Card key={m.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Sigma className="w-5 h-5 text-primary shrink-0" />
                    <CardTitle className="truncate">{m.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" aria-label={t('Edit custom metric')} title={t('Edit custom metric')} onClick={() => openEdit(m)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" aria-label={t('Delete custom metric')} title={t('Delete custom metric')} onClick={() => setDeleteTarget(m)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-xs">{m.code}</Badge>
                  <Badge variant="secondary" className="text-xs">{formatLabel(m.format)}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <Label className="text-xs text-muted-foreground">{t('Formula')}</Label>
                <p className="text-xs font-mono bg-surface-2 border border-border p-2 rounded mt-1 break-all">
                  {m.formula}
                </p>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) {
            setEditing(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Edit custom metric')}</DialogTitle>
            <DialogDescription>
              {t('Update the formula, format or decimals.')}
            </DialogDescription>
          </DialogHeader>
          {renderForm()}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button onClick={handleUpdate}>{t('Save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('Delete custom metric')}
        description={t('This permanently deletes the metric column from reports.')}
        confirmLabel={t('Delete')}
        onConfirm={() => handleDelete(deleteTarget && deleteTarget.id)}
      />
    </div>
  );
}
